import React, { useEffect, useReducer, useRef, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import BootstrapTable, {
    ColumnDescription,
    SortOrder,
    TableChangeState,
    TableChangeType,
    SelectRowProps,
} from "react-bootstrap-table-next";
import { LoadingOverlay, Loader } from "react-overlay-loader";
import { FolderColumnName, SortOrder as EnumSortOrder, ShareFolderTypes } from "src/Core/Common/Enums";
import Checkbox, { CheckBoxSize } from "../Common/ui/Checkbox";
import { getFolderModelColumns, getRetentionOptions, isFolderOwner } from "src/helper/HelperFunctions";

import { IFolderModel } from "src/Core/ViewModels/Folder/FolderModel";
import { IRetentionPeriod } from "src/Core/ViewModels/Company/Settings/RetentionSettings";

import FolderActionsPopUp from "../Message/Common/FolderActionPopUp";
import DropdownMenu from "react-bootstrap/esm/DropdownMenu";
import DropdownToggle from "react-bootstrap/esm/DropdownToggle";
import { getFormattedLocalDateTime } from "src/helper/DateHelperFunctions";
import { ArchiveConstants, DateFormatConstants } from "src/Core/Common/Constants";
import { FolderTypeIcon } from "src/assets/images/svg/FolderIcons";
import { useSelector } from "react-redux";
import { ApplicationState } from "src/store";
import FolderBreadCrumb from "../Common/Navigation/FolderBreadCrumb";

interface IArchiveFolderTableProps {
    folderData: IFolderModel[];
    selectedFolders: IFolderModel[];
    isContentLoading: boolean;
    retentionPeriods?: IRetentionPeriod[];
    currentFolder?: IFolderModel;
    updateFolderSelection: (folders: IFolderModel[]) => void;
    requestFolders?: (sortOptions: { sortColumn: FolderColumnName; sortOrder: EnumSortOrder }) => void;
    handleFolderClick: (
        folder: IFolderModel,
        sortOptions: { sortColumn: FolderColumnName; sortOrder: EnumSortOrder },
        isTitleBreadcrumb: boolean,
        breadCrumbArrayArchiveTable: IFolderModel[]
    ) => void;
    setReload: (value: boolean) => void;
    onHide?: () => void;
    goToParentFolder?: (value: boolean) => void;
    goToFolder: (folderId: number, breadcrumbIndex: number, isTitleBreadcrumb: boolean) => void;
}

export interface IFolderFilterOptions {
    sortBy?: string;
    sortOrder?: SortOrder;
}

export const ArchiveFolderTable: React.FC<IArchiveFolderTableProps> = props => {
    const [sortOptions, setSortOptions] = useState<IFolderFilterOptions>();
    const user = useSelector((state: ApplicationState) => state.userProfile);
    const mailboxUserData = useSelector((state: ApplicationState) => state.commonState.mailboxUserData);
    const ref = useRef();
    const defaultSorted: [{ dataField: string; order: SortOrder }] | undefined = [
        {
            dataField: (sortOptions && sortOptions.sortBy) ?? "folderName",
            order: sortOptions && sortOptions.sortOrder ? sortOptions.sortOrder : "asc",
        },
    ];

    const getBreadcrumb = (folder: IFolderModel) => {
        const folderIds = folder.parentFolderIds.split(", ").map(Number);
        const breadCrumbArray: IFolderModel[] = [];
        folder.path?.split(" > ").forEach((x, index) => {
            const folderId = folderIds[index];
            breadCrumbArray.push({
                folderName: x,
                folderId,
                parentId: index == 0 ? 0 : folderIds[index - 1],
                path: folder.path,
                parentFolderIds: folder.parentFolderIds,
                expiresDays: folder.expiresDays,
                createdBy: folder.createdBy,
                createdDate: folder.createdDate,
                owner: folder.owner,
                description: folder.description,
                folderType: folder.folderType,
                permissionGroupId: folder.permissionGroupId,
                isPermissiomInherited: folder.isPermissiomInherited,
                isPrivate: folder.isPrivate,
            });
        });
        return breadCrumbArray;
    };

    const folderNameFormatter = (cell: string, row: IFolderModel): JSX.Element => {
        return (
            <div ref={ref} className="table-cell-with-controls folder-table">
                <div className="flex-row">
                    <span className="margin-right-2">
                        <FolderTypeIcon height={34} width={34} folderType={row.folderType} />
                    </span>
                    <div className="flex-coloumn cell-wrapper">
                        <Button
                            variant="link"
                            className="cell-folder-name"
                            title={cell}
                            onClick={e => {
                                e.stopPropagation();
                                resetSelection();
                                props.handleFolderClick(
                                    row,
                                    {
                                        sortColumn: sortOptions
                                            ? getFolderModelColumns(sortOptions.sortBy)
                                            : FolderColumnName.FOLDERNAME,
                                        sortOrder:
                                            sortOptions && sortOptions.sortOrder === "asc"
                                                ? EnumSortOrder.ASC
                                                : sortOptions
                                                    ? EnumSortOrder.ASC
                                                    : EnumSortOrder.DESC,
                                    },
                                    !row.path,
                                    row.path ? getBreadcrumb(row) : []
                                );
                            }}
                        >
                            {cell}
                        </Button>

                        {row.path ? (
                            <div className="cell-date " title={row.path}>
                                <FolderBreadCrumb
                                    breadCrumbArray={getBreadcrumb(row)}
                                    goToFolder={props.goToFolder}
                                    isTitleBreadcrumb={false}
                                />
                            </div>
                        ) : (
                            <span className="cell-date ellipsis">
                                {getFormattedLocalDateTime(row.createdDate, DateFormatConstants.DEFAULT_DATE_FORMAT)}
                            </span>
                        )}
                    </div>
                </div>
                {folderToolbar(cell, row)}
            </div>
        );
    };

    const folderToolbar = (cell: string, row: IFolderModel): JSX.Element => {
        return (
            <Dropdown drop="start" className="inline-Actions-Wrapper">
                <DropdownToggle as={Button}>
                    <i className="fas fa-ellipsis-h cursor-pointer inline-actions-trigger" />
                </DropdownToggle>
                <DropdownMenu>
                    <Dropdown.Item>
                        <FolderActionsPopUp
                            selectedFolders={[row]}
                            retentionOptions={getRetentionOptions(props.retentionPeriods)}
                            setReload={props.setReload}
                            goToParentFolder={props.goToParentFolder}
                            onHide={props.onHide}
                            isInlineLevel={true}
                            currentFolder={props.currentFolder}
                        />
                    </Dropdown.Item>
                </DropdownMenu>
            </Dropdown>
        );
    };

    const columns: ColumnDescription[] = [
        {
            text: "Name",
            dataField: "folderName",
            sort: true,
            formatter: folderNameFormatter,
            style: { width: "calc(100vw - 70px)", overflow: "initial" },
        },
    ];

    const onTableChange = (type: TableChangeType, newState: TableChangeState<IFolderModel>) => {
        switch (type) {
            case "sort":
                setSortOptions({
                    sortBy: newState.sortField,
                    sortOrder: newState.sortOrder,
                });
                break;
        }

        props.requestFolders({
            sortColumn: sortOptions ? getFolderModelColumns(newState.sortField) : FolderColumnName.FOLDERNAME,
            sortOrder: newState.sortOrder === "asc" ? EnumSortOrder.ASC : EnumSortOrder.DESC,
        });
    };

    const setNoContent = () => {
        if (props.isContentLoading) {
            return (
                <LoadingOverlay style={{ height: 400 }}>
                    <Loader loading={props.isContentLoading} />
                </LoadingOverlay>
            );
        } else {
            return ArchiveConstants.NO_MESSAGES_OR_REQUESTS;
        }
    };

    const resetSelection = () => {
        props.updateFolderSelection([]);
    };

    const handleOnSelect = (row: IFolderModel, isSelect: boolean) => {
        let selectedFolders = [...props.selectedFolders];
        if (isSelect) {
            selectedFolders.push(row);
        } else {
            selectedFolders = selectedFolders.filter(c => c.folderId != row.folderId);
        }
        props.updateFolderSelection(selectedFolders);
    };

    const handleOnSelectAll = (isSelect: boolean, rows: IFolderModel[]) => {
        if (isSelect) {
            props.updateFolderSelection(rows);
        } else {
            resetSelection();
        }
    };

    const enableEditPermission = (folder?: IFolderModel) => {
        if (
            !folder ||
            isFolderOwner(folder, user, mailboxUserData) ||
            (folder.permissionGroupId == ShareFolderTypes.EDIT && folder.isPermissiomInherited)
        ) {
            return true;
        } else if (folder.permissionGroupId == ShareFolderTypes.VIEW) {
            return false;
        } else if (folder.permissionGroupId === ShareFolderTypes.NONE && folder.isPermissiomInherited) {
            return true;
        }
        return false;
    };

    const enableSubFolderEditPermission = (folder: IFolderModel) => {
        if (folder.parentId === 0 || !folder.isPermissiomInherited) {
            return true;
        } else {
            return false;
        }
    };

    const nonSelectableFolders = props.folderData
        .filter(
            x =>
                (x.permissionGroupId === ShareFolderTypes.VIEW && !enableEditPermission(x)) ||
                (x.permissionGroupId === ShareFolderTypes.EDIT && enableSubFolderEditPermission(x))
        )
        .map(y => y.folderId);

    const selectRow: SelectRowProps<IFolderModel> = {
        mode: "checkbox",
        clickToSelect: true,
        onSelect: handleOnSelect,
        onSelectAll: handleOnSelectAll,
        selected: props.selectedFolders.map(f => f.folderId),
        nonSelectable: nonSelectableFolders,
        selectionHeaderRenderer: () => {
            return (
                <div className="checkbox-container">
                    <Checkbox
                        id="mpiCheckbox"
                        size={CheckBoxSize.sm}
                        checked={!!props.selectedFolders.length}
                        indeterminate={
                            props.folderData.length - nonSelectableFolders.length !== props.selectedFolders.length
                        }
                        onClick={e => e.stopPropagation()}
                        onChange={e => e.stopPropagation()}
                        text=""
                    />
                </div>
            );
        },
    };

    return (
        <div className="archive-folder-table-container">
            <BootstrapTable
                id="customTable"
                parentClassName="archive-table"
                keyField="folderId"
                columns={columns}
                data={props.folderData}
                remote
                defaultSorted={defaultSorted}
                selectRow={selectRow}
                onTableChange={onTableChange}
                noDataIndication={setNoContent()}
                bordered={false}
                key={props.currentFolder.expiresDays}
            />
        </div>
    );
};

ArchiveFolderTable.displayName = ArchiveFolderTable.name;

export default ArchiveFolderTable;
